import { Breakpoints } from '@angular/cdk/layout';
import { Component, Inject, Input, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { EMPTY, from, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { ClassName } from '@sim/role';
import { RoleService } from '@sim/role/services/role.service';
import {
  BaseTableComponent,
  makeQueryBuilder,
  makeQueryRules, QueryBuilderRule,
  TableContextMenu,
  TableField,
  UiTableComponent,
} from '@ui/ui.module';

import { CardsApiService, filePath, SwalService } from '@atmi/core';
import { Base, CommonCard } from '@atmi/core/models';
import { CardOAFiltersMeta } from '@src/app/core/models/cards/card-oa';

type BaseConcrete = Base & { id: string };

@Component({
  selector: 'app-base-cards',
  styleUrls: ['./base-cards.component.scss'],
  templateUrl: './base-cards.component.html',
})
export class BaseCardsComponent
  extends BaseTableComponent<CommonCard>
  implements UiTableComponent<CommonCard>, OnDestroy {

  private readonly base$ = new Subject<Base>();
  @Input() set base(base: Base | null) {
    if (base) {
      this.base$.next(base);
    }
  }

  readonly fields: TableField<CommonCard>[] = [
    {
      context: (x) => x.previewUrl ? `${this.fileUrl}${x.previewUrl}` : undefined,
      header: this.translate.instant('Anteprima'),
      key: 'previewUrl',
      template: 'img',
    },
    {
      header: this.translate.instant('Codice unico'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'codiceUnico',
    },
    {
      context: (x) => x.SGT.SGTT,
      header: this.translate.instant('Titolo soggetto'),
      key: 'SGT.SGTT',
    },
    {
      header: this.translate.instant('Fornitore'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'fornitore',
    },
    {
      header: this.translate.instant('Data creazione'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'createdAt',
      template: 'date-time',
    },
    {
      align: 'center',
      click: (row) => this.router.navigate([`cards/${row.TSK.toLowerCase()}/manager`, row.id]),
      header: '',
      key: 'link',
      template: 'link',
      width: 50,
    },
  ];

  readonly pager$ = this.base$.pipe(
    filter((base): base is BaseConcrete => !!base),
    map((base) => ((page?: number, limit?: number) => {
      const rules = makeQueryRules({ base: base.id }, CardOAFiltersMeta);
      const query = makeQueryBuilder(rules);

      return this.cardsApi.indexOA({ page, limit, filter: query });
    })),
  );

  readonly contextMenus: Array<TableContextMenu<CommonCard>> = [];

  constructor(
    protected readonly translate: TranslateService,
    protected readonly router: Router,
    protected readonly route: ActivatedRoute,
    protected readonly swalService: SwalService,
    private readonly cardsApi: CardsApiService,
    private readonly roleService: RoleService,
    @Inject(filePath) private readonly fileUrl: string,
  ) {
    super(translate, router, route, swalService);

    const hasCapability = (['BeneOA', 'BeneF', 'BeneMI', 'BeneD', 'BeneS'] as ClassName[]).some(
      (className) => this.roleService.hasCapability(className, ['update'])
    );

    if (hasCapability) {
      this.contextMenus.push({
        action: (item) => this.router.navigate([`cards/${item.TSK.toLowerCase()}/manager`, item.id]),
        label: this.translate.instant('Modifica'),
      });
    } else {
      this.contextMenus.push({
        action: (item) => this.router.navigate([`cards/${item.TSK.toLowerCase()}/manager`, item.id]),
        label: this.translate.instant('Visualizza'),
      });
    }
  }

  ngOnDestroy() {
    this.base$.complete();
  }

  // tslint:disable-next-line:variable-name
  deleteConcreteHandler(_id: string) {
    return EMPTY;
  }

  // tslint:disable-next-line:variable-name
  deleteSelectedConcreteHandler(_ids: string[]) {
    return from(Promise.all([]));
  }
}
